import {
  QueryFunction,
  QueryKey,
  UseQueryResult,
  useQuery,
} from "@tanstack/react-query";
import { Crop, DropSize, Task, TillingDepth } from "./constants";
import { karlFarmPost } from "./queryClient";

if (process.env.NEXT_PUBLIC_KARL_MASTER_API_KEY == null) {
  throw new Error("NEXT_PUBLIC_KARL_MASTER_API_KEY is not set.");
}
const MASTER_API_KEY = process.env.NEXT_PUBLIC_KARL_MASTER_API_KEY;

// Route: POST /ai/scores
// API Docs: https://documenter.getpostman.com/view/16381164/UVksNF2z#61774111-ea01-432f-9c0d-ff1e528bca26

export const scoresStaleTime = 1000 * 60 * 60 * 2; // 2 hours

export const getUseScoresQueryKey = (
  fieldId: string,
  start: number,
  duration: number,
  task: Task,
  crop?: Crop,
  tillingDepth?: TillingDepth,
  dropSize?: DropSize,
  includeWeatherData = false
) => {
  return [
    "/ai/scores",
    fieldId,
    start,
    duration,
    task,
    crop,
    tillingDepth,
    dropSize,
    includeWeatherData,
  ];
};

export const getUseScoresQueryFunction = (
  fieldId: string,
  start: number,
  duration: number,
  task: Task,
  crop?: Crop,
  tillingDepth?: TillingDepth,
  dropSize?: DropSize,
  includeWeatherData = false
): QueryFunction<ScoresResponse, QueryKey> => {
  const queryFunction: QueryFunction<ScoresResponse, QueryKey> = () => {
    return karlFarmPost<ScoresResponse>(
      "/ai/scores",
      {
        field_info: { id: fieldId },
        task_info: {
          attributes: {
            task: task,
            crop,
            tilling_depth: tillingDepth,
            drop_size: dropSize,
          },
          time: {
            start,
            duration,
          },
        },
      },
      {
        ...(includeWeatherData
          ? {
              headers: {
                "x-api-key": MASTER_API_KEY, // If the master api key is used, the response will include weather data.
              },
            }
          : {}),
      }
    );
  };

  return queryFunction;
};

export const useScores: (params: {
  fieldId: string;
  start: number;
  duration: number;
  task: Task;
  crop?: Crop;
  tillingDepth?: TillingDepth;
  dropSize?: DropSize;
  includeWeatherData?: boolean;
}) => UseQueryResult<ScoresResponse, unknown> = ({
  fieldId,
  start,
  duration,
  task,
  crop,
  tillingDepth,
  dropSize,
  includeWeatherData = false,
}) => {
  return useQuery<unknown, unknown, ScoresResponse>(
    getUseScoresQueryKey(
      fieldId,
      start,
      duration,
      task,
      crop,
      tillingDepth,
      dropSize,
      includeWeatherData
    ),
    getUseScoresQueryFunction(
      fieldId,
      start,
      duration,
      task,
      crop,
      tillingDepth,
      dropSize,
      includeWeatherData
    ),
    {
      enabled: task !== "other",
      staleTime: scoresStaleTime,
    }
  );
};

export const dateToScore = (scores: Score[], date: Date): Score | undefined => {
  return scores.find((score) => {
    const timeInUTCSeconds = date.getTime() / 1000;
    const timeToNearestHour = Math.floor(timeInUTCSeconds / 60 / 60) * 60 * 60;
    return score.timestamp === timeToNearestHour;
  });
};

export type ScoresResponse = {
  messages: { status: string };
  data?: Score[];
};

export type Score = {
  timestamp: number;
  value: number;
  description: Conditions;
};

export type Conditions = Partial<
  Record<
    ConditionType,
    {
      Detail: boolean; // Describes if we should use the "high" descriptions from the translations files. true = high, false = low
      description: Level;
    }
  >
>;
export type ConditionType =
  | "clouds"
  | "gusts"
  | "precipitation"
  | "soil_temperature"
  | "soil_moisture"
  | "temperature"
  | "wind";
export type Level = "good" | "moderate" | "optimal" | "poor" | "critical";
